import { Component, OnInit, inject, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ModalService } from '@card-software/library';
import { SiteService } from '@card-software/nexus-utilities';
import { DepartmentModalComponent } from '@app/components/modals/department-modal/department-modal.component';
import { Department } from '@app/models/department-manager/department';
import { DepartmentsProxyService } from '@app/proxies/departments.proxy.service';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import { ToastService } from '@app/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-departments',
  templateUrl: './departments.component.html',
  styleUrl: './departments.component.scss',
})
export class DepartmentsComponent implements OnInit {
  private readonly departmentProxyService = inject(DepartmentsProxyService);
  private readonly modalService = inject(ModalService);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  departments = signal<Department[]>([]);
  private allDepartments = signal<Department[]>([]);
  isLoading = signal(0);

  searchControl = new FormControl('', { nonNullable: true });

  ngOnInit(): void {
    this.getAllDepartments();
    this.setupSearchFunctionality();
  }

  private incrementLoading() {
    this.isLoading.update((v) => v + 1);
  }

  private decrementLoading() {
    this.isLoading.update((v) => v - 1);
  }

  private getAllDepartments() {
    this.incrementLoading();
    this.departmentProxyService
      .getAllDepartments()
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe((response) => {
        if (!response.data) return;
        this.allDepartments.set(response.data);
        this.departments.set([...response.data]);
      });
  }

  private setupSearchFunctionality() {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchTerm) => this.filterDepartments(searchTerm));
  }

  private filterDepartments(searchTerm: string) {
    if (!searchTerm.trim()) {
      this.departments.set([...this.allDepartments()]);
      return;
    }

    const term = searchTerm.toLowerCase().trim();

    this.departments.set(
      this.allDepartments().filter((department) => {
        const nameEnMatch = department.name.en?.toLowerCase().includes(term);
        const nameEsMatch = department.name.es?.toLowerCase().includes(term);
        const descEnMatch = department.description.en?.toLowerCase().includes(term);
        const descEsMatch = department.description.es?.toLowerCase().includes(term);
        return nameEnMatch || nameEsMatch || descEnMatch || descEsMatch;
      }),
    );
  }

  openModalDepartment(department: Department | null) {
    this.modalService.open(DepartmentModalComponent, { modalClass: 'modal_small' }, { deparment: department }).subscribe((result) => {
      if (result?.action === 'save') {
        this.getAllDepartments();
        this.toast.success(
          this.translate.instant('Common_Success'),
          this.translate.instant(department ? 'Departments_Updated_Successfully' : 'Departments_Created_Successfully'),
        );
      }
    });
  }

  clearSearch() {
    this.searchControl.setValue('');
  }

  getMembersCount(department: Department): number {
    const siteId = SiteService.getSelectedSiteId();
    const siteProps = department.siteProperties?.find((sp) => sp.siteId === siteId) ?? department.siteProperties?.[0];
    return siteProps?.members?.length ?? 0;
  }

  getManagersCount(department: Department): number {
    const siteId = SiteService.getSelectedSiteId();
    const siteProps = department.siteProperties?.find((sp) => sp.siteId === siteId) ?? department.siteProperties?.[0];
    return siteProps?.managers?.length ?? 0;
  }
}
